import { AppTheme } from './interfaces';
import { MAIN_PALETTE, NEUTRAL_PALETTE, NEUTRALS_PALETTE } from './palette';

export const COMMON_THEME: Omit<AppTheme, 'name'> = {
  colors: {
    mainColor: '#DE3CF2',
    main: { ...MAIN_PALETTE },
    neutrals: { ...NEUTRAL_PALETTE },
    primary: {
      c900: '#20162C',
      c800: '#552267',
      c700: '#8A3196',
      c600: '#AC21BE',
      c500: '#DE3CF2',
      c400: '#D48BEE',
      c300: '#D1BAEF',
      c200: '#E8D6FF',
      c100: '#F1E7FF',
      c050: '#FBF9FF',
    },
    greyscale: {
      c900: '#1A171E',
      c800: '#27232C',
      c700: '#372F45',
      c600: '#40374F',
      c500: '#524A5C',
      c400: '#746E7B',
      c300: '#96919B',
      c200: '#B8B2BE',
      c100: '#DCD7E0',
      c050: '#F4F1F5',
    },
    system: {
      error: '#EB274B',
      success: '#4DD072',
      warning: '#FABF1C',
      info: '#2D7FF9',
      information: '#4DD072',
      'black-overlay': 'rgba(24, 17, 38, 0.3)',
    },
    layout: {
      background: '#FCFAFF',
      surface: {
        default: '#FFFFFF',
        success: '#EFFBF2',
        error: '#FCE3E8',
      },
      overlay: `${NEUTRAL_PALETTE.c900}80`, // 80 represent opacity of 50%
      divider: NEUTRAL_PALETTE.c100,
    },
    content: {
      default: NEUTRAL_PALETTE.c900,
      subdued: {
        high: NEUTRAL_PALETTE.c500,
        low: NEUTRAL_PALETTE.c400,
      },
      disabled: NEUTRAL_PALETTE.c500,
      white: '#FFFFFF',
      success: '#31BA57',
      error: '#EB274B',
    },
    secondary: {
      'gradient-menu': 'linear-gradient(89.72deg, #21172D 0.19%, #2A1F37 99.72%);',
      '30-light-blue': 'rgba(225, 229, 254, 0.3)',
      '20-light-blue': 'rgba(225, 229, 254, 0.2)',
      '12-light-blue': '#FBF9FF',
      // NOTE: warning: gradient uses a rotation that is not supported by CSS and may not behave as expected
      fill: 'radial-gradient(106.89% 169.31% at -1.42% 2.96%, rgba(171, 135, 220, 0.22) 0%, rgba(135, 73, 217, 0.22) 44.81%, rgba(209, 186, 239, 0.22) 100%), #1A171E;',
      stroke: {
        // Because of limited CSS support, opting to use pseudo elements that compose the "layers" of radial gradients.
        1: 'radial-gradient(circle, rgba(113,66,174,1) 0%, rgba(231,212,255,0) 86%)',
        2: 'radial-gradient(circle, rgba(233,216,255,1) 0%, rgba(124,47,226,0) 91%)',
      },
    },
    black: '#1A171E',
    white: '#FFFFFF',
    background: '#FBF9FF',
    selection: '#ECFAC6',
    lightBlackBackground: '#223445',
    red: '#ED0A34',
    darkRed: '#EB4747',
    neutralsPalette: {
      black: NEUTRALS_PALETTE.black,
      white: NEUTRALS_PALETTE.white,
    },
  },
  gradients: {
    primary: 'linear-gradient(236.09deg, #D600F1 15.33%, #A200FA 87.48%)',
    overflowGradient: 'linear-gradient(270deg, #ffffff 0%, #ffffff00 100%)',
    horizontalOverflowGradient:
      'linear-gradient(270deg, #fff 21.52%, rgba(255, 255, 255, 0.5) 59.37%, rgba(255, 255, 255, 0) 87.33%)',
    scrollGradient: 'linear-gradient(180deg,rgba(255,255,255,0.2) 0%,#ffffff80 5.76%,#ffffff 100%)',
    shimmer: 'linear-gradient(to right, #f6f6f6 8%, #f0f0f0 18%, #f6f6f6 33%);',
    button: {
      default: 'linear-gradient(236.09deg, #D600F1 15.33%, #A200FA 87.48%)',
      hover: 'linear-gradient(236.09deg, #E442F9 15.33%, #BA3DFD 87.48%)',
      pressed: 'linear-gradient(236.09deg, #D600F1 15.33%, #A200FA 87.48%)',
    },
    buttonsContainer: {
      default: 'linear-gradient(180deg, rgba(248, 248, 248, 0) 0%, #F8F8F8 63.02%)',
    },
    colors: {
      primary: '#7528BE',
      secondary: '#7C12D3',
      tertiary: '#DDBEFE',
    },
  },
  fonts: {
    primary: 'IBM Plex Sans',
  },
  fontSize: {
    xxs: '0.625rem',
    xs: '0.75rem',
    s: '0.875rem',
    m: '0.9375rem',
    ml: '1rem',
    mlg: '1.125rem',
    l: '1.25rem',
    lxl: '1.375rem',
    xl: '1.5rem',
    '2xl': '1.875rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
  },
  lineHeight: {
    xxs: '0.875rem',
    xs: '1.25rem',
    s: '1.375rem',
    m: '1.5rem',
    ml: '1.25rem',
    mlg: '1.5rem',
    l: '1.75rem',
    lxl: '1.875rem',
    xl: '2rem',
    '2xl': '2.5rem',
    '3xl': '2.5rem',
    '4xl': '3.75rem',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  fontVariants: {
    desktop: {
      h1: {
        fontSize: '2rem',
        lineHeight: '2.5rem',
      },
      h2: {
        fontSize: '1.5rem',
        lineHeight: '1.75rem',
      },
      h3: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
      },
      h4: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: '1.125rem',
      },
      buttons: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
      },
      caption1: {
        fontSize: '0.75rem',
        lineHeight: '1rem',
      },
      caption2: {
        fontSize: '0.625rem',
        lineHeight: '0.875rem',
      },
    },
    mobile: {
      h1: {
        fontSize: '1.5rem',
        lineHeight: '2rem',
      },
      h2: {
        fontSize: '1.25rem',
        lineHeight: '1.5rem',
      },
      h3: {
        fontSize: '1.0625rem',
        lineHeight: '1.5rem',
      },
      h4: {
        fontSize: '0.9375rem',
        lineHeight: '1.25rem',
      },
      body1: {
        fontSize: '0.9375rem',
        lineHeight: '1.25rem',
      },
      body2: {
        fontSize: '0.8125rem',
        lineHeight: '1.25rem',
      },
      buttons: {
        fontSize: '0.9375rem',
        lineHeight: '1.25rem',
      },
      caption1: {
        fontSize: '0.6875rem',
        lineHeight: '1rem',
      },
      caption2: {
        fontSize: '0.625rem',
        lineHeight: '0.875rem',
      },
    },
  },
  breakpoints: {
    smallMobile: '375px',
    mobile: '480px',
    minMobile: '480px',
    xlMobile: '598px',
    tablet: '768px',
    desktop: '1054px',
    large: '1200px',
    'extra-large': '1400px',
  },
  borderRadius: '0.25rem',
  shadows: {
    primary: '0rem 0.125rem 0.25rem 0rem #0308150a',
    secondary: '0rem .0625rem .25rem 0rem rgba(41, 12, 55, 0.1)',
    hover: '0rem 0.375rem 0.5rem 0rem rgba(87, 46, 101, 0.10)',
    chatBubble: '0px -4px 32px 0px rgba(0, 0, 0, 0.08)',
    bottomNavBar: '0px -8px 32px 0px #3f4b131a',
    box: '0px 1px 4px 0px #290c371a',
    pill: '0px 10px 30px 0px rgba(0, 0, 0, 0.1)',
    navigationHighlight: '0px 1px 7px 0px rgba(124, 18, 211, 0.50)',
    tooltips: '0px 3px 5px 0px #00000008, 0px 10px 20px 0px #4829680D',
    menu: '0px 3px 5px 0px rgba(0, 0, 0, 0.03), 0px 10px 20px 0px rgba(72, 41, 104, 0.05)',
  },
};
